// App.jsx — gate, then route.
//
// The gate is the whole authorisation story on the client: signed out → sign-in
// screen; signed in but not a superuser → auth.js already refused and cleared
// the session, so the sign-in screen explains why. The backend enforces the
// same rule on every /admin route; this only decides what a person sees.

import React from 'react';
import { useAuth } from './AuthProvider';
import { useRoute } from './router';
import { api } from './api';
import { useAsync } from './useAsync';
import Shell from './ui/Shell';
import SignInScreen, { BootSplash } from './ui/SignInScreen';
import Overview from './screens/Overview';
import Orgs from './screens/Orgs';
import OrgDetail from './screens/OrgDetail';
import Users from './screens/Users';
import Monitor from './screens/Monitor';
import Engine from './screens/Engine';
import Support from './screens/Support';
import Cartons from './screens/Cartons';

function Routed({ route }) {
  const [head] = route.parts;
  switch (head) {
    case undefined: return <Overview />;
    case 'orgs':
      return route.parts[1]
        ? <OrgDetail orgId={route.parts[1]} />
        : <Orgs />;
    case 'users': return <Users query={route.query} />;
    case 'cartons': return <Cartons query={route.query} />;
    case 'monitor': return <Monitor query={route.query} />;
    case 'engine': return <Engine query={route.query} />;
    case 'support': return <Support query={route.query} />;
    default: return <Overview />;
  }
}

const ACTIVE = { undefined: 'overview', orgs: 'orgs', users: 'users', cartons: 'cartons',
                 monitor: 'monitor', support: 'support' };

export default function App() {
  const { signedIn, authorized, resuming, error } = useAuth();
  const route = useRoute();

  // One overview poll drives the tab-bar badges. It lives here rather than in
  // Shell so the Overview screen and the badge cannot disagree about how many
  // failures there are.
  const ov = useAsync(() => api.overview(24), [], { skip: !authorized });
  React.useEffect(() => {
    if (!authorized) return undefined;
    const t = setInterval(ov.reload, 120000);
    return () => clearInterval(t);
  }, [authorized, ov.reload]);

  if (resuming) return <BootSplash />;
  if (!signedIn || !authorized) return <SignInScreen initialError={error} />;

  const badges = {
    monitor: ov.data?.jobs?.failed || 0,
  };

  return (
    <Shell active={ACTIVE[route.parts[0]] || 'overview'} badges={badges}>
      <Routed route={route} />
    </Shell>
  );
}
